import { Controller } from "@hotwired/stimulus"

// Manages browser push notification permission and subscription.
// Reads VAPID public key from <meta name="vapid-public-key">.
// Targets: button (toggle), status (text display)
export default class extends Controller {
  static targets = ["button", "status"]

  connect() {
    this.updateUI()
  }

  async toggle() {
    if (!this.supported) {
      this.setStatus("Push notifications not supported in this browser.")
      return
    }

    const permission = Notification.permission

    if (permission === "granted") {
      await this.unsubscribe()
    } else if (permission === "denied") {
      this.setStatus("Notifications blocked. Please enable them in browser settings.")
    } else {
      await this.subscribe()
    }

    this.updateUI()
  }

  async subscribe() {
    try {
      const perm = await Notification.requestPermission()
      if (perm !== "granted") {
        this.setStatus("Permission denied.")
        return
      }

      const reg = await navigator.serviceWorker.ready
      const vapidKey = this.vapidPublicKey
      if (!vapidKey) {
        this.setStatus("Push configuration missing.")
        return
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
      })

      const keys = subscription.toJSON()
      const token = document.querySelector('meta[name="csrf-token"]')?.content

      await fetch("/push_subscriptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        body: JSON.stringify({
          endpoint: keys.endpoint,
          p256dh: keys.keys.p256dh,
          auth: keys.keys.auth
        })
      })

      this.setStatus("Push notifications enabled.")
    } catch (err) {
      console.error("[PushPermission] subscribe failed:", err)
      this.setStatus("Failed to enable push notifications.")
    }
  }

  async unsubscribe() {
    try {
      const reg = await navigator.serviceWorker.ready
      const subscription = await reg.pushManager.getSubscription()

      if (subscription) {
        const endpoint = subscription.endpoint
        await subscription.unsubscribe()

        const token = document.querySelector('meta[name="csrf-token"]')?.content
        await fetch("/push_subscriptions", {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token
          },
          credentials: "same-origin",
          body: JSON.stringify({ endpoint })
        })
      }

      this.setStatus("Push notifications disabled.")
    } catch (err) {
      console.error("[PushPermission] unsubscribe failed:", err)
      this.setStatus("Failed to disable push notifications.")
    }
  }

  updateUI() {
    if (!this.hasButtonTarget) return

    if (!this.supported) {
      this.buttonTarget.disabled = true
      this.buttonTarget.textContent = "Not Supported"
      return
    }

    const permission = Notification.permission
    if (permission === "granted") {
      this.buttonTarget.textContent = "Disable Push Notifications"
      this.buttonTarget.classList.remove("btn-primary")
      this.buttonTarget.classList.add("btn-ghost")
    } else if (permission === "denied") {
      this.buttonTarget.disabled = true
      this.buttonTarget.textContent = "Notifications Blocked"
    } else {
      this.buttonTarget.textContent = "Enable Push Notifications"
      this.buttonTarget.classList.remove("btn-ghost")
      this.buttonTarget.classList.add("btn-primary")
    }
  }

  setStatus(msg) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = msg
    }
  }

  get supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  get vapidPublicKey() {
    return document.querySelector('meta[name="vapid-public-key"]')?.content
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
